import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Users, Plus, ChevronRight } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

export default function Groups() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [groups, setGroups] = useState([]);
  const [filter, setFilter] = useState("all"); // all | mine
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    setErr("");
    try {
      const r = await api.get(filter === "mine" ? "/groups/mine" : "/groups");
      setGroups(r.data);
    } catch (e) {
      setErr(formatApiError(e));
    }
  }, [filter]);

  useEffect(() => {
    load();
  }, [load]);

  const toggleJoin = async (g) => {
    setBusy(g.id);
    try {
      if (g.joined) await api.post(`/groups/${g.id}/leave`);
      else await api.post(`/groups/${g.id}/join`);
      await load();
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-6 fade-up">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-[#d4af37] text-xs uppercase tracking-[0.3em]">Groups · 兴趣群组</p>
          <h1 className="font-serif text-4xl mt-2">Find your tribe.</h1>
          <p className="text-white/50 mt-1">Join a group to see its posts, broadcasts and chat.</p>
        </div>
        {user?.role === "admin" && (
          <Link
            to="/admin/groups"
            className="hidden sm:flex items-center gap-2 border border-white/10 hover:border-[#d4af37]/50 px-3 py-2 text-xs uppercase tracking-widest text-white/70 hover:text-[#d4af37]"
            data-testid="groups-admin-link"
          >
            <Plus size={14} strokeWidth={1.5} /> New Group
          </Link>
        )}
      </div>

      <div className="flex gap-1 border-b border-white/10">
        {[
          ["all", `All · ${filter === "all" ? groups.length : ""}`],
          ["mine", "Mine"],
        ].map(([t, label]) => (
          <button
            key={t}
            onClick={() => setFilter(t)}
            className={`px-4 py-3 text-xs uppercase tracking-[0.2em] -mb-px border-b ${
              filter === t ? "border-[#d4af37] text-[#d4af37]" : "border-transparent text-white/50"
            }`}
            data-testid={`groups-tab-${t}`}
          >
            {label.trim()}
          </button>
        ))}
      </div>

      {err && <p className="text-red-400 text-sm" data-testid="groups-error">{err}</p>}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="groups-list">
        {groups.length === 0 && (
          <p className="text-white/40 col-span-full" data-testid="groups-empty">
            {filter === "mine" ? "You haven't joined any groups yet." : "No groups yet."}
          </p>
        )}
        {groups.map((g) => (
          <div
            key={g.id}
            className="group border border-white/10 hover:border-[#d4af37]/40 transition rounded-md p-5 bg-[#141414]"
            data-testid={`group-card-${g.hobby_key || g.id}`}
          >
            <div className="flex items-start justify-between gap-3">
              <button onClick={() => nav(`/groups/${g.id}`)} className="text-left flex-1 min-w-0" data-testid={`group-open-${g.id}`}>
                <div className="font-serif text-xl truncate group-hover:text-[#d4af37] transition">{g.name}</div>
                <div className="text-xs text-white/50 mt-1 line-clamp-2">{g.description}</div>
                <div className="flex items-center gap-2 mt-3 text-[10px] uppercase tracking-widest text-white/40">
                  <Users size={12} strokeWidth={1.5} />
                  {g.member_count} members
                  {g.is_official && (
                    <span className="ml-2 text-[#d4af37]">· Official</span>
                  )}
                </div>
              </button>
              <button
                onClick={() => toggleJoin(g)}
                disabled={busy === g.id}
                className={`shrink-0 px-3 py-2 text-[10px] uppercase tracking-widest border transition ${
                  g.joined
                    ? "border-white/15 text-white/60 hover:border-red-400/40 hover:text-red-300"
                    : "border-[#d4af37]/60 text-[#d4af37] hover:bg-[#d4af37] hover:text-black"
                }`}
                data-testid={`group-toggle-${g.id}`}
              >
                {g.joined ? "Leave" : "Join"}
              </button>
            </div>
            <button
              onClick={() => nav(`/groups/${g.id}`)}
              className="mt-3 text-[10px] uppercase tracking-widest text-white/40 hover:text-[#d4af37] flex items-center gap-1"
              data-testid={`group-arrow-${g.id}`}
            >
              Open <ChevronRight size={12} strokeWidth={1.5} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
